/*
  Make the collection grid scroll with every image already there.

  Native loading="lazy" only begins fetching once an image nears the viewport, and
  the page gets no say in how near. That is why cards kept arriving blank on a fast
  flick (reported 2026-08-28, iPhone 15 Pro on wifi, 160 products per page).

  A moving preload window was tried first and was not enough: it makes images fast
  once they appear, but they still appear only as you reach them. So this warms the
  WHOLE grid instead, in two passes:

    1. Near pass  — an IntersectionObserver ~2 screens out fetches what you are
                    about to reach, so the top of the page is right immediately.
    2. Full pass  — after load, a small queue works through every remaining card
                    image in document order, 4 at a time. ~160 images at a ~28 KB
                    median is roughly 4.5 MB, a few seconds on wifi, after which
                    the rest of the session scrolls with nothing left to fetch.

  Why this sticks: the images are same-origin and Shopify serves them
  `cache-control: public, max-age=31557600`, so a warmed image is served from the
  browser cache when the real <img> is finally allowed to load.

  The <img> elements in the document are never touched. They keep loading="lazy"
  and simply paint from cache. Nothing here can change what renders, and with JS
  off, or if this throws, behaviour is exactly the native one.

  Data note: the full pass deliberately downloads images the visitor may never
  scroll to. That is the trade being made for an unbroken scroll. It is skipped
  under Save-Data and on 2g/3g where it would be antisocial — though note that
  iOS Safari implements neither signal, so on iPhone the full pass always runs.
*/
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) return;

  var conn = navigator.connection;
  if (conn && conn.saveData) return;

  var GRID_ID = 'ProductGridContainer';
  // The card's own image only. The quick-add modal carries a hidden thumbnail-sized
  // copy per card; warming those would double the requests for nothing on screen.
  var SELECTOR = '.card__media img';
  var CONCURRENCY = 4;

  var warmed = new WeakSet();
  var observer = null;
  var queue = [];
  var active = 0;
  var fullPassStarted = false;

  function warm(img, onDone) {
    if (warmed.has(img)) { if (onDone) onDone(); return; }
    warmed.add(img);
    if (img.complete && img.naturalWidth) { if (onDone) onDone(); return; }
    // Deliberately unreferenced once fired: the fetch populates the HTTP cache and
    // the element is then collectable, so we never hold 160 decoded bitmaps alive.
    var pre = new Image();
    if (onDone) {
      pre.onload = onDone;
      pre.onerror = onDone;
    }
    if (img.sizes) pre.sizes = img.sizes;
    if (img.srcset) pre.srcset = img.srcset;
    if (img.src) pre.src = img.src;
  }

  function pump() {
    while (active < CONCURRENCY && queue.length) {
      var img = queue.shift();
      if (warmed.has(img)) continue;
      active++;
      warm(img, function () {
        active--;
        pump();
      });
    }
  }

  function getObserver() {
    if (observer) return observer;
    // Two screens for the near pass. Enough that the first screens are covered
    // before the full pass has had time to finish.
    var screens = 2;
    var eff = conn && conn.effectiveType;
    if (eff === 'slow-2g' || eff === '2g' || eff === '3g') screens = 1;
    observer = new IntersectionObserver(
      function (entries, obs) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          obs.unobserve(entries[i].target);
          warm(entries[i].target);
        }
      },
      { rootMargin: Math.round((window.innerHeight || 800) * screens) + 'px 0px' }
    );
    return observer;
  }

  function images() {
    var grid = document.getElementById(GRID_ID);
    return grid ? Array.prototype.slice.call(grid.querySelectorAll(SELECTOR)) : [];
  }

  function nearPass() {
    var obs = getObserver();
    var imgs = images();
    for (var i = 0; i < imgs.length; i++) {
      if (!warmed.has(imgs[i])) obs.observe(imgs[i]);
    }
  }

  function fullPass() {
    var eff = conn && conn.effectiveType;
    if (eff === 'slow-2g' || eff === '2g' || eff === '3g') return;
    var imgs = images();
    for (var i = 0; i < imgs.length; i++) {
      if (!warmed.has(imgs[i])) queue.push(imgs[i]);
    }
    pump();
  }

  function startFullPass() {
    if (fullPassStarted) return;
    fullPassStarted = true;
    fullPass();
  }

  // Deliberately NOT waiting for window 'load'. This page pulls ~88 scripts plus
  // Shopify analytics and the 12 eager card images, so 'load' can be many seconds
  // out on a phone — the full pass had barely begun by the time anyone scrolled,
  // which is why warming everything appeared to change nothing. Start on a short
  // timer instead, and start immediately if the visitor scrolls before that.
  function scheduleFullPass() {
    var kick = function () {
      window.removeEventListener('scroll', kick);
      startFullPass();
    };
    window.addEventListener('scroll', kick, { passive: true, once: true });
    setTimeout(kick, 600);
  }

  function start() {
    if (!document.getElementById(GRID_ID)) return; // not a collection page
    nearPass();
    scheduleFullPass();

    // facets.js replaces the grid wholesale via innerHTML on every filter or sort
    // change, which drops both passes with the old nodes. Re-run on replacement
    // rather than patching facets.js, so this stays one self-contained file.
    var grid = document.getElementById(GRID_ID);
    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () {
        nearPass();
        fullPassStarted = false;
        startFullPass();   // grid was replaced by a filter; warm the new set now
      }, 200);
    }).observe(grid, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
